import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  collection,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  deleteDoc
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuBuilding2, LuChevronDown, LuFolderKanban, LuLayers, LuLoaderCircle, LuMap, LuMapPinned, LuMousePointerClick, LuPalette, LuPlus, LuSave, LuTrash2, LuUserCheck, LuUsers, LuX } from 'react-icons/lu';
import { db } from '../../config/firebaseConfig';
import { useTheme } from '../../contexts/ThemeContext';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import IconButton from '../../components/ui/IconButton';
import { SelectInput, TextInput } from '../../components/form/FormControls';
import { EmptyState } from '../ProfilePage/components/ProfileUi';
import TurkeyRegionMap from './TurkeyRegionMap';
import {
  normalizeCity,
  findRegionByCity,
  normalizeManagerIds,
  REGION_COLORS
} from './regionUtils';
import useCenterText from './centerText';
import { Pill, RichText } from './parts/centerBits';

let tempIdCounter = 0;
const nextTempId = () => `temp-${++tempIdCounter}`;

/**
 * Bölge Yönetimi (Tailwind): provinces are assigned to regions on the map of
 * Türkiye; each region has a colour, a regional manager and optional
 * province-specific managers, with center / worker / project counts from the
 * projects. Nothing is written before "Kaydet". The logic is the old one.
 */
const RegionManagementModal = ({ isOpen, onClose, employeesMap, onSaved, centers = [] }) => {
  const { isDarkMode } = useTheme();
  const { t, language, label, richText } = useCenterText();
  // Toasts read the current language through a ref (the loaders run on open only)
  const labelRef = useRef(label);
  labelRef.current = label;
  const msg = (key, vars) => labelRef.current(`toast.${key}`, vars);
  const [regions, setRegions] = useState([]);
  const [deletedRegionIds, setDeletedRegionIds] = useState([]);
  const [selectedRegionId, setSelectedRegionId] = useState(null);
  const [selectedCityName, setSelectedCityName] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedRegionIds, setExpandedRegionIds] = useState({});
  // centerId -> { workers: {id: name}, projects: {id: title} } derived from Projects
  const [centerAssignments, setCenterAssignments] = useState({});

  const employeeOptions = Object.entries(employeesMap)
    .map(([id, name]) => ({ id, name }))
    .filter((emp) => emp.name)
    .sort((a, b) => a.name.localeCompare(b.name, 'tr'));

  useEffect(() => {
    if (!isOpen) return;

    const fetchRegions = async () => {
      setLoading(true);
      try {
        const snapshot = await getDocs(collection(db, 'Regions'));
        const data = snapshot.docs.map((d) => {
          const region = { id: d.id, ...d.data() };
          return {
            ...region,
            cityManagerIds: region.cityManagerIds || {}
          };
        });
        setRegions(data);
        setDeletedRegionIds([]);
        setSelectedRegionId(data.length > 0 ? data[0].id : null);
        setSelectedCityName('');
      } catch (error) {
        console.error('Error fetching regions:', error);
        toast.error(msg('regionsLoadError'));
      } finally {
        setLoading(false);
      }
    };

    fetchRegions();
  }, [isOpen]);

  // Load project assignments once per open, to show worker/project counts per center
  useEffect(() => {
    if (!isOpen) return;

    const fetchAssignments = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'Projects'));
        const assignments = {};
        snapshot.docs.forEach((projectDoc) => {
          const data = projectDoc.data();
          const title = data.titleShort || data.titleLong || projectDoc.id;
          (data.employees || []).forEach((emp) => {
            if (!emp.centerId) return;
            if (!assignments[emp.centerId]) {
              assignments[emp.centerId] = { workers: {}, projects: {} };
            }
            if (emp.id) {
              assignments[emp.centerId].workers[emp.id] =
                `${emp.name || ''} ${emp.surname || ''}`.trim();
            }
            assignments[emp.centerId].projects[projectDoc.id] = title;
          });
        });
        setCenterAssignments(assignments);
      } catch (error) {
        console.error('Error fetching project assignments:', error);
      }
    };

    fetchAssignments();
  }, [isOpen]);

  const getCentersForCity = (cityName) => {
    const normalized = normalizeCity(cityName);
    return centers.filter((c) => normalizeCity(c['İl']) === normalized);
  };

  const getCentersForRegion = (region) =>
    (region.cities || []).flatMap((city) => getCentersForCity(city));

  // Unique worker/project counts across a set of centers
  const aggregateStats = (centerList) => {
    const workers = {};
    const projects = {};
    centerList.forEach((center) => {
      const a = centerAssignments[center.id];
      if (!a) return;
      Object.assign(workers, a.workers);
      Object.assign(projects, a.projects);
    });
    return {
      centerCount: centerList.length,
      workerCount: Object.keys(workers).length,
      projectCount: Object.keys(projects).length,
      projectNames: Object.values(projects)
    };
  };

  // City-level details for the map tooltip
  const getCityDetails = (cityName) => {
    const cityCenters = getCentersForCity(cityName);
    const stats = aggregateStats(cityCenters);
    return {
      ...stats,
      centers: cityCenters.map((center) => {
        const a = centerAssignments[center.id] || { workers: {}, projects: {} };
        return {
          id: center.id,
          name: center['Adı'] || '',
          workerCount: Object.keys(a.workers).length,
          projectCount: Object.keys(a.projects).length
        };
      })
    };
  };

  const toggleRegionExpand = (regionId, event) => {
    event.stopPropagation();
    setExpandedRegionIds((prev) => ({ ...prev, [regionId]: !prev[regionId] }));
  };

  const handleAddRegion = () => {
    const usedColors = regions.map((r) => r.color);
    const color =
      REGION_COLORS.find((c) => !usedColors.includes(c)) ||
      REGION_COLORS[regions.length % REGION_COLORS.length];

    const newRegion = {
      id: nextTempId(),
      isNew: true,
      name: `Bölge ${regions.length + 1}`,
      color,
      managerId: '',
      cityManagerIds: {},
      cities: []
    };
    setRegions((prev) => [...prev, newRegion]);
    setSelectedRegionId(newRegion.id);
    setSelectedCityName('');
  };

  const updateRegion = (regionId, changes) => {
    setRegions((prev) =>
      prev.map((r) => (r.id === regionId ? { ...r, ...changes } : r))
    );
  };

  const handleDeleteRegion = (regionId) => {
    const region = regions.find((r) => r.id === regionId);
    if (!region) return;

    setRegions((prev) => prev.filter((r) => r.id !== regionId));
    if (!region.isNew) {
      setDeletedRegionIds((prev) => [...prev, regionId]);
    }
    if (selectedRegionId === regionId) {
      setSelectedRegionId(null);
      setSelectedCityName('');
    }
  };

  const selectRegion = (regionId) => {
    setSelectedRegionId(regionId);
    setSelectedCityName('');
  };

  const selectCity = (regionId, cityName, event) => {
    event.stopPropagation();
    setSelectedRegionId(regionId);
    setSelectedCityName(cityName);
  };

  const addCityManager = (regionId, cityName, employeeId) => {
    if (!cityName || !employeeId) return;
    const cityKey = normalizeCity(cityName);

    setRegions((prev) =>
      prev.map((region) => {
        if (region.id !== regionId) return region;
        const currentManagers = normalizeManagerIds(region.cityManagerIds?.[cityKey]);
        if (currentManagers.includes(employeeId)) return region;

        return {
          ...region,
          cityManagerIds: {
            ...(region.cityManagerIds || {}),
            [cityKey]: [...currentManagers, employeeId]
          }
        };
      })
    );
  };

  const removeCityManager = (regionId, cityName, employeeId, event) => {
    event.stopPropagation();
    const cityKey = normalizeCity(cityName);

    setRegions((prev) =>
      prev.map((region) => {
        if (region.id !== regionId) return region;
        const nextManagers = normalizeManagerIds(region.cityManagerIds?.[cityKey])
          .filter((id) => id !== employeeId);
        const nextCityManagerIds = { ...(region.cityManagerIds || {}) };

        if (nextManagers.length > 0) {
          nextCityManagerIds[cityKey] = nextManagers;
        } else {
          delete nextCityManagerIds[cityKey];
        }

        return {
          ...region,
          cityManagerIds: nextCityManagerIds
        };
      })
    );
  };

  const handleCityClick = (city) => {
    if (!selectedRegionId) {
      toast.info(msg('pickRegionFirst'));
      return;
    }

    const normalized = normalizeCity(city.name);
    const currentRegion = findRegionByCity(regions, city.name);
    const alreadyInSelectedRegion = currentRegion?.id === selectedRegionId;
    setSelectedCityName(alreadyInSelectedRegion ? '' : city.name);

    setRegions((prev) =>
      prev.map((region) => {
        const withoutCity = (region.cities || []).filter(
          (c) => normalizeCity(c) !== normalized
        );
        const cityManagerIds = { ...(region.cityManagerIds || {}) };
        delete cityManagerIds[normalized];

        if (region.id === selectedRegionId) {
          // Toggle: remove if already in the selected region, otherwise add
          return {
            ...region,
            cityManagerIds,
            cities: alreadyInSelectedRegion ? withoutCity : [...withoutCity, city.name]
          };
        }
        // A city can belong to only one region — remove from others
        return { ...region, cityManagerIds, cities: withoutCity };
      })
    );
  };

  const getCityManagers = (region, cityName) =>
    normalizeManagerIds(region.cityManagerIds?.[normalizeCity(cityName)]);

  const handleSave = async () => {
    const unnamed = regions.find((r) => !r.name?.trim());
    if (unnamed) {
      toast.error(msg('regionNameRequired'));
      return;
    }

    setSaving(true);
    try {
      for (const regionId of deletedRegionIds) {
        await deleteDoc(doc(db, 'Regions', regionId));
      }

      for (const region of regions) {
        const payload = {
          name: region.name.trim(),
          color: region.color,
          managerId: region.managerId || '',
          cityManagerIds: Object.entries(region.cityManagerIds || {}).reduce((acc, [cityKey, managerIds]) => {
            const cityInRegion = (region.cities || []).some((city) => normalizeCity(city) === cityKey);
            const normalizedManagers = normalizeManagerIds(managerIds);
            if (cityInRegion && normalizedManagers.length > 0) {
              acc[cityKey] = normalizedManagers;
            }
            return acc;
          }, {}),
          cities: region.cities || [],
          updatedAt: new Date()
        };

        if (region.isNew) {
          await addDoc(collection(db, 'Regions'), {
            ...payload,
            createdAt: new Date()
          });
        } else {
          await updateDoc(doc(db, 'Regions', region.id), payload);
        }
      }

      toast.success(msg('regionsSaved'));
      if (onSaved) onSaved();
      onClose();
    } catch (error) {
      console.error('Error saving regions:', error);
      toast.error(msg('regionsSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const assignedCityCount = regions.reduce(
    (sum, r) => sum + (r.cities?.length || 0),
    0
  );
  const selectedRegion = regions.find((r) => r.id === selectedRegionId);
  const stopEvent = (e) => e.stopPropagation();
  const hint = selectedRegion
    ? richText('regions.hintSelected', { region: selectedRegion.name || '—' })
    : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      title={label('regions.title')}
      subtitle={label('regions.subtitle')}
      closeLabel={t('common.close')}
      lang={language}
      bodyClassName="tw-p-0"
      icon={(
        <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="xs">
          <LuMap aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>{label('common.cancel')}</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || loading} className="max-sm:tw-flex-1">
            {saving
              ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
              : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {saving ? label('regions.saving') : label('regions.save')}
          </Button>
        </>
      )}
    >
      <div className="lg:tw-grid lg:tw-grid-cols-[minmax(0,1fr)_minmax(22rem,26rem)]">
        {/* Map: stays in view on wide screens while the region list scrolls */}
        <div style={{ '--region': selectedRegion?.color || '#0d9488' }} className="tw-space-y-3 tw-p-4 sm:tw-p-6 lg:tw-sticky lg:tw-top-0 lg:tw-self-start">
          <p className="tw-flex tw-items-start tw-gap-2 tw-rounded-2xl tw-bg-teal-500/[0.07] tw-px-3.5 tw-py-2.5 tw-text-sm tw-leading-snug tw-text-teal-900 dark:tw-text-teal-100">
            <LuMousePointerClick aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-teal-600 dark:tw-text-teal-300" />
            <span className="tw-min-w-0">
              {hint ? (
                <RichText
                  segments={hint}
                  boldClassName="tw-font-semibold tw-text-[color:var(--region)]"
                />
              ) : label('regions.hintNone')}
            </span>
          </p>

          <div>
            <TurkeyRegionMap
              regions={regions}
              selectedRegionId={selectedRegionId}
              onCityClick={handleCityClick}
              employeesMap={employeesMap}
              isDarkMode={isDarkMode}
              getCityDetails={getCityDetails}
            />
          </div>

          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-2">
            <Pill icon={LuMapPinned}>{label('regions.assigned', { count: assignedCityCount })}</Pill>
            <Pill icon={LuLayers}>{label('regions.regionCount', { count: regions.length })}</Pill>
          </div>
        </div>

        <div className="tw-border-t tw-border-slate-200/80 tw-bg-slate-50/70 lg:tw-min-h-full lg:tw-border-l lg:tw-border-t-0 dark:tw-border-white/10 dark:tw-bg-white/[0.02]">
          <div className="tw-sticky tw-top-0 tw-z-10 tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-b tw-border-slate-200/70 tw-bg-slate-50/90 tw-px-4 tw-py-3 tw-backdrop-blur-md sm:tw-px-5 dark:tw-border-white/10 dark:tw-bg-[#22262e]/90">
            <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
              {label('regions.listTitle')}
              <span className="tw-rounded-full tw-bg-slate-900/[0.06] tw-px-2 tw-py-0.5 tw-text-xs tw-tabular-nums tw-text-slate-500 dark:tw-bg-white/10 dark:tw-text-slate-300">{regions.length}</span>
            </h3>
            <Button size="sm" onClick={handleAddRegion}>
              <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" strokeWidth={2.5} />
              {label('regions.add')}
            </Button>
          </div>

          <div className="tw-space-y-3 tw-p-3 sm:tw-p-4">
            {loading ? (
              <p className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-10 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
                <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
                {label('regions.loading')}
              </p>
            ) : regions.length === 0 ? (
              <EmptyState icon={LuMap} title={label('regions.empty')} compact />
            ) : (
              regions.map((region) => {
                const regionCenters = getCentersForRegion(region);
                const regionStats = aggregateStats(regionCenters);
                const isExpanded = !!expandedRegionIds[region.id];
                const cityList = [...(region.cities || [])].sort((a, b) => a.localeCompare(b, 'tr'));
                const selectedCityBelongsToRegion = selectedCityName &&
                  cityList.some((cityName) => normalizeCity(cityName) === normalizeCity(selectedCityName));
                const showCityManagerPanel = selectedRegionId === region.id && cityList.length > 0;
                const selectedCityManagers = selectedCityBelongsToRegion
                  ? getCityManagers(region, selectedCityName)
                  : [];
                const availableCityManagers = employeeOptions.filter(
                  (emp) => !selectedCityManagers.includes(emp.id)
                );
                const isSelected = selectedRegionId === region.id;
                return (
                  <div
                    key={region.id}
                    role="group"
                    tabIndex={0}
                    aria-label={region.name || label('regions.namePlaceholder')}
                    aria-current={isSelected ? 'true' : undefined}
                    style={{ '--region': region.color }}
                    onClick={() => selectRegion(region.id)}
                    onKeyDown={(e) => {
                      if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        selectRegion(region.id);
                      }
                    }}
                    className={`tw-relative tw-cursor-pointer tw-space-y-3 tw-overflow-hidden tw-rounded-2xl tw-border tw-p-3.5 tw-pl-5 tw-transition-[box-shadow,background-color,border-color] tw-duration-200 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${isSelected ? 'tw-border-transparent tw-bg-white tw-shadow-[0_0_0_2px_var(--region),0_16px_36px_-20px_rgba(15,23,42,0.45)] dark:tw-bg-[#262b34]' : 'tw-border-slate-200/80 tw-bg-white/70 hover:tw-bg-white dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:hover:tw-bg-white/[0.05]'}`}
                  >
                    <span aria-hidden="true" className={`tw-absolute tw-inset-y-0 tw-left-0 tw-bg-[color:var(--region)] tw-transition-[width] ${isSelected ? 'tw-w-2' : 'tw-w-1.5'}`} />

                    <div className="tw-flex tw-items-center tw-gap-2">
                      <label onClick={stopEvent} className="tw-relative tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-cursor-pointer tw-place-items-center tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200 tw-bg-[color:var(--region)] tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.4)] dark:tw-border-white/15" title={label('regions.color')}>
                        <LuPalette aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-white tw-drop-shadow" />
                        <input
                          type="color"
                          value={region.color}
                          aria-label={label('regions.color')}
                          onClick={stopEvent}
                          onChange={(e) => updateRegion(region.id, { color: e.target.value })}
                          className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-cursor-pointer tw-opacity-0"
                        />
                      </label>
                      <TextInput
                        value={region.name}
                        placeholder={label('regions.namePlaceholder')}
                        aria-label={label('regions.namePlaceholder')}
                        onClick={stopEvent}
                        onChange={(e) => updateRegion(region.id, { name: e.target.value })}
                        className="tw-h-9 tw-min-w-0 tw-flex-1 tw-font-semibold"
                      />
                      <IconButton
                        label={label('regions.delete')}
                        tone="alert"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRegion(region.id);
                        }}
                        className="tw-shrink-0"
                      >
                        <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />
                      </IconButton>
                    </div>

                    <div className="tw-flex tw-items-center tw-gap-2" onClick={stopEvent}>
                      <LuUserCheck aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400" />
                      <span className="tw-min-w-0 tw-flex-1">
                        <SelectInput
                          value={region.managerId || ''}
                          aria-label={label('regions.managerPlaceholder')}
                          onChange={(e) => updateRegion(region.id, { managerId: e.target.value })}
                          placeholder={label('regions.managerPlaceholder')}
                          options={employeeOptions.map((emp) => ({ value: emp.id, label: emp.name }))}
                          className="tw-h-9 tw-text-sm"
                        />
                      </span>
                    </div>

                    <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                      {(region.cities || []).length === 0 ? (
                        <span className="tw-text-xs tw-italic tw-text-slate-400 dark:tw-text-slate-500">{label('regions.noCities')}</span>
                      ) : (
                        cityList.map((cityName) => {
                          const managerCount = getCityManagers(region, cityName).length;
                          const isSelectedCity = selectedRegionId === region.id &&
                            normalizeCity(cityName) === normalizeCity(selectedCityName);
                          return (
                            <button
                              key={cityName}
                              type="button"
                              aria-pressed={isSelectedCity}
                              title={label('regions.cityHint')}
                              onClick={(e) => selectCity(region.id, cityName, e)}
                              className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${isSelectedCity ? 'tw-bg-[color:var(--region)] tw-text-white tw-shadow-sm' : 'tw-bg-slate-900/[0.05] tw-text-slate-700 hover:tw-bg-slate-900/[0.09] dark:tw-bg-white/[0.07] dark:tw-text-slate-200 dark:hover:tw-bg-white/[0.12]'}`}
                            >
                              {cityName}
                              {managerCount > 0 && (
                                <span
                                  title={label('regions.cityManagerCount', { count: managerCount })}
                                  className={`tw-grid tw-h-4 tw-min-w-[1rem] tw-place-items-center tw-rounded-full tw-px-1 tw-text-[10px] tw-font-bold tw-leading-none ${isSelectedCity ? 'tw-bg-white/25 tw-text-white' : 'tw-bg-[color:var(--region)] tw-text-white'}`}
                                >
                                  {managerCount}
                                </span>
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>

                    {showCityManagerPanel && (
                      <div className="tw-space-y-2.5 tw-rounded-xl tw-border tw-border-slate-200/80 tw-bg-slate-50 tw-p-3 dark:tw-border-white/10 dark:tw-bg-black/15" onClick={stopEvent}>
                        {selectedCityBelongsToRegion ? (
                          <>
                            <div className="tw-flex tw-flex-wrap tw-items-baseline tw-justify-between tw-gap-x-2 tw-gap-y-0.5">
                              <span className="tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
                                {label('regions.cityManagers', { city: selectedCityName })}
                              </span>
                              <small className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                                {selectedCityManagers.length > 0
                                  ? label('regions.managerCount', { count: selectedCityManagers.length })
                                  : label('regions.noCityManagers')}
                              </small>
                            </div>

                            <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                              {selectedCityManagers.length === 0 ? (
                                <span className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                                  {region.managerId
                                    ? label('regions.fallback', { name: employeesMap[region.managerId] || '-' })
                                    : label('regions.noneYet')}
                                </span>
                              ) : (
                                selectedCityManagers.map((managerId) => (
                                  <span key={managerId} className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-py-0.5 tw-pl-2.5 tw-pr-0.5 tw-text-xs tw-font-medium tw-text-slate-700 dark:tw-border-white/10 dark:tw-bg-white/[0.06] dark:tw-text-slate-200">
                                    {employeesMap[managerId] || managerId}
                                    <button
                                      type="button"
                                      onClick={(e) => removeCityManager(region.id, selectedCityName, managerId, e)}
                                      aria-label={label('regions.removeManager')}
                                      title={label('regions.removeManager')}
                                      className="tw-grid tw-h-5 tw-w-5 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 tw-transition-colors hover:tw-bg-rose-500/10 hover:tw-text-rose-600 dark:hover:tw-text-rose-300"
                                    >
                                      <LuX aria-hidden="true" className="tw-h-3 tw-w-3" />
                                    </button>
                                  </span>
                                ))
                              )}
                            </div>

                            <SelectInput
                              value=""
                              aria-label={label('regions.addManager')}
                              disabled={availableCityManagers.length === 0}
                              onChange={(e) => addCityManager(region.id, selectedCityName, e.target.value)}
                              placeholder={availableCityManagers.length === 0 ? label('regions.noMoreManagers') : label('regions.addManager')}
                              options={availableCityManagers.map((emp) => ({ value: emp.id, label: emp.name }))}
                              className="tw-h-9 tw-text-sm"
                            />
                          </>
                        ) : (
                          <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('regions.pickCity')}</p>
                        )}
                      </div>
                    )}

                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
                      <span title={label('regions.centersHint')}><Pill icon={LuBuilding2}>{label('regions.centers', { count: regionStats.centerCount })}</Pill></span>
                      <span title={label('regions.workersHint')}><Pill icon={LuUsers}>{label('regions.workers', { count: regionStats.workerCount })}</Pill></span>
                      <span title={label('regions.projectsHint')}><Pill icon={LuFolderKanban}>{label('regions.projects', { count: regionStats.projectCount })}</Pill></span>
                      {regionCenters.length > 0 && (
                        <IconButton
                          label={isExpanded ? label('regions.hideCenters') : label('regions.showCenters')}
                          aria-expanded={isExpanded}
                          onClick={(e) => toggleRegionExpand(region.id, e)}
                          className="tw-ml-auto tw-h-8 tw-w-8"
                        >
                          <LuChevronDown aria-hidden="true" className={`tw-h-4 tw-w-4 tw-transition-transform tw-duration-200 ${isExpanded ? 'tw-rotate-180' : ''}`} />
                        </IconButton>
                      )}
                    </div>

                    {isExpanded && (
                      <ul className="tw-space-y-1.5 tw-border-t tw-border-slate-200/70 tw-pt-3 dark:tw-border-white/10">
                        {regionCenters.map((center) => {
                          const a = centerAssignments[center.id] || { workers: {}, projects: {} };
                          const workerNames = Object.values(a.workers);
                          return (
                            <li key={center.id} className="tw-rounded-xl tw-bg-slate-900/[0.03] tw-px-3 tw-py-2 dark:tw-bg-white/[0.03]">
                              <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
                                <LuBuilding2 aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-slate-400" />
                                <span className="tw-min-w-0 tw-truncate">{center['Adı']}</span>
                                <span className="tw-shrink-0 tw-text-xs tw-font-normal tw-text-slate-500 dark:tw-text-slate-400">({center['İl']})</span>
                              </div>
                              <div className="tw-mt-0.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                                {label('regions.centerMeta', { workers: workerNames.length, projects: Object.keys(a.projects).length })}
                              </div>
                              {workerNames.length > 0 && (
                                <div className="tw-mt-1 tw-text-xs tw-leading-relaxed tw-text-slate-600 dark:tw-text-slate-300">
                                  {workerNames.sort((x, y) => x.localeCompare(y, 'tr')).join(', ')}
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

RegionManagementModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  employeesMap: PropTypes.object.isRequired,
  onSaved: PropTypes.func,
  centers: PropTypes.arrayOf(PropTypes.object),
};

export default RegionManagementModal;
